import { Injectable } from '@angular/core';
import { daysheetorderitem } from './daysheet-order.service';
import { byName } from 'src/globals';

//One line of an itinerary sheet: a booking in one of the resources it uses. A booking that uses
//three resources produces three of these, which is what puts it in three places on the sheet.
export interface itineraryrow {
  resourceId: string;
  resourceName: string;
  //Stamped on at build time from the resource list - a booking's own copy of a resource does
  //not carry one. See buildRows.
  displayOrder: number;
  timeslot: string;
  slotSort: number;
  bookingId: string;
  bookingIdHR: string;
  startTime: string;
  startDateTime: string;
  name: string;
  email: string;
  phone: string;
  packageName: string;
  //Carried so a screen can look the package up - its colour, for one, which BookingList does
  //not return.
  packageId: string;
  resourceLabel: string;
  endtime: string;
  itinerary: string;
  guests: number;
  //People on this resource specifically, which is what fills the room. guests is the whole
  //booking, and the two differ when only part of the party uses a resource.
  people: number;
  admissions: string;
  waiver: string;
  extras: string;
  outstanding: number;
  currency: string;
  checkedin: boolean;
  //Set on the row that starts a timeslot, for its sub-heading. Zero on every other row.
  slotBookings: number;
  slotPeople: number;
  slotCapacity: number;
  //Carried so the views can mark a row the way the other booking lists do.
  notes: string;
  allergy: string;
  publicBooking: boolean;
  disabled: boolean;
  //Set on the first line of each timeslot inside a resource, so the sub-heading is drawn once.
  firstOfSlot: boolean;
}

//Turns BookingList answers into itinerary lines. Shared by the Itinerary Report, which covers a
//period, and the Itinerary view, which covers one day - they show the same sheet, so they work
//the rows out the same way.
@Injectable({
  providedIn: 'root'
})
export class ItineraryService {

  //The columns both screens export to CSV.
  readonly cols: any[] = [
    { field: 'resourceName', header: 'Resource' },
    { field: 'timeslot', header: 'Timeslot' },
    { field: 'endtime', header: 'Finishes' },
    { field: 'bookingIdHR', header: 'Sales ID' },
    { field: 'startDateTime', header: 'Start Date/Time' },
    { field: 'name', header: 'Name' },
    { field: 'email', header: 'Email' },
    { field: 'phone', header: 'Phone' },
    { field: 'packageName', header: 'Package' },
    { field: 'resourceLabel', header: 'Resource Slot' },
    { field: 'itinerary', header: 'Original Itinerary' },
    { field: 'guests', header: 'Guests' },
    { field: 'admissions', header: 'Admissions' },
    { field: 'extras', header: 'Extras' },
    { field: 'outstanding', header: 'Outstanding' },
    { field: 'waiver', header: 'Waiver' },
  ];

  //What BookingList has to be asked for. Both screens send the same thing bar the dates.
  searchCriteria(venueid: string, startdate: any, enddate: any, tags: any[]): any {
    return {
      venueid: venueid,
      startdate: startdate,
      enddate: enddate,
      tags: tags ?? [],
      disabled: false,
      created: true,
      //Both added to BookingList so neither screen has to make a call per booking. They bring
      //back the resources array and the signed waivers on each booking.
      returnresources: true,
      returnwaivers: true
    };
  }

  /*
    Resource id to its place, so sections read in the same order as the Resources screen rather
    than alphabetically.

    Taken from the position the Resources screen saves - its drag handle posts ResourceReorder
    with a displayorder per resource - rather than from where the resource happens to sit in the
    response. The two screens make the same call and that screen applies no sort of its own, so
    the order it shows and the order this got were coming out different: the response's own
    order cannot be relied on. Read in each spelling the API uses for the field, the way the id
    has to be, and a resource with no position at all keeps the place it arrived in.
  */
  orderOf(resources: any[]): Map<string, number> {
    const order = new Map<string, number>();

    const positionOf = (res: any, arrived: number): number => {
      //Not orderindex. That is on every resource too, but it is a resource's slot inside a
      //package - the package editor fills it with start time offsets - and has nothing to do
      //with the Resources screen. Reading it as a position sorts the sections by junk.
      const saved = res?.displayorder ?? res?.displayOrder;
      const place = Number(saved);

      return saved == null || isNaN(place) ? arrived : place;
    };

    (resources ?? [])
      .map((res, arrived) => ({ res: res, place: positionOf(res, arrived), arrived: arrived }))
      .sort((one, two) => (one.place - two.place) || (one.arrived - two.arrived))
      .forEach((entry, place) => {
        const id = this.idOf(entry.res);

        if (id != "") {
          order.set(id, place);
        }

        //Keyed by name as well. The id is the right key and is tried first, but the API does
        //not spell a resource's id the same way on a booking as it does on the resource list,
        //and anything the map cannot find drops to the bottom of the report sorted by name -
        //which is indistinguishable from the sections simply being alphabetical. The name is
        //what the section heading shows, so it always has something to match on.
        const name = this.nameKey(entry.res?.name);

        //First one wins, so two resources sharing a name both take the earlier section.
        if (name != "" && !order.has(name)) {
          order.set(name, place);
        }
      });

    return order;
  }

  /*
    Which resources the itinerary shows and the order of its sections, both taken from the
    Daysheet Resource Order page (see DaysheetOrderService) - the same rule the Daysheet draws by
    (see its normalLayout):
    - a resource switched on there is shown, in its own place
    - a resource group switched on there shows every resource in it, in the group's order, at
      the group's place - including members switched off on their own
    - a resource the order has never heard of (added since it was last saved) is shown, after
      everything else
    - anything else is left off

    visible answers by id first, then by name, for the reason orderOf keys both. A resource it
    has no answer for is shown.
  */
  fromDaysheetOrder(items: daysheetorderitem[]): { order: Map<string, number>, visible: Map<string, boolean> } {
    const order = new Map<string, number>();
    const visible = new Map<string, boolean>();

    const nameOf = new Map<string, string>();
    (items ?? [])
      .filter(item => item.propertytype == "RESOURCE")
      .forEach(item => nameOf.set(item.propertyid, item.name));

    //Id first, so a name only fills in where no id did.
    const set = (map: Map<string, any>, id: string, value: any) => {
      if (id != "" && !map.has(id)) { map.set(id, value); }

      const name = this.nameKey(nameOf.get(id));

      if (name != "" && !map.has(name)) { map.set(name, value); }
    };

    let place = 0;

    (items ?? []).forEach(item => {
      if (!item.showondaysheet) { return; }

      const ids = item.propertytype == "RESOURCE" ? [item.propertyid] : item.members;

      ids.forEach(id => {
        set(order, id, place++);
        set(visible, id, true);
      });
    });

    //Everything the order knows about and has not shown by now is switched off.
    (items ?? [])
      .filter(item => item.propertytype == "RESOURCE")
      .forEach(item => set(visible, item.propertyid, false));

    return { order: order, visible: visible };
  }

  //Whether the Daysheet order shows a resource. Anything it has no answer for is shown.
  private shownBy(visible: Map<string, boolean>, id: string, name: string): boolean {
    return visible.get(id) ?? visible.get(this.nameKey(name)) ?? true;
  }

  //The resources the itinerary can show, alphabetically, for its Resources filter. From the
  //resource list the Daysheet order was built from; with no order, by Show in Itinerary.
  shownResources(items: daysheetorderitem[], visible: Map<string, boolean>): any[] {
    return byName((items ?? [])
      .filter(item => item.propertytype == "RESOURCE" && item.resource != null)
      .map(item => item.resource)
      .filter(res => visible != null
        ? this.shownBy(visible, this.idOf(res), this.resourceName(res.name))
        : res.showinitinerary !== false));
  }

  //Where a resource sits in the Resources screen's order. By id where both sides agree on one,
  //by name where they do not - the API does not spell a resource's id the same way on a booking
  //as it does on the resource list. Anything matching neither sorts to the end.
  private placeIn(resourceOrder: Map<string, number>, id: string, name: string): number {
    if (resourceOrder == null) { return Number.MAX_SAFE_INTEGER; }

    return resourceOrder.get(id)
      ?? resourceOrder.get(this.nameKey(name))
      ?? Number.MAX_SAFE_INTEGER;
  }

  /*
    Puts rows that are already built into their resource's place.

    The bookings and the resource list are asked for together, so either can land first. The
    rows are built as soon as the bookings arrive - waiting on the resource list would mean an
    empty report whenever that call is slow, and no report at all if it fails - and this re-
    stamps them if the order turns up afterwards.
  */
  stampOrder(rows: itineraryrow[], resourceOrder: Map<string, number>) {
    (rows ?? []).forEach(row => {
      row.displayOrder = this.placeIn(resourceOrder, row.resourceId, row.resourceName);
    });
  }

  //Name keys are prefixed so a resource whose id happens to read like another's name cannot
  //collide with it in the same map.
  private nameKey(value: any): string {
    const name = (value ?? "").toString().trim().toLowerCase();

    return name == "" ? "" : "name:" + name;
  }

  /*
    A resource's id, spelled either of the two ways the API spells it.

    ResourceList returns it as resourceId. A booking's own copy of a resource returns it as
    resourceid, all lower case. Both have to come out of here the same or nothing keys together:
    the order map is built from one and read with the other, so every lookup misses, sections
    fall back to sorting by name, the resource filter matches nothing and the capacities and
    colours keyed the same way never find their resource. The daysheet reads both spellings for
    this reason - see its closingsForDay.

    id is the last resort: on a booking's resource it is that row, not the resource.
  */
  idOf(res: any): string {
    return (res?.resourceId ?? res?.resourceid ?? res?.id ?? "").toString();
  }

  /*
    resourceOrder puts the sections in order - from fromDaysheetOrder (the Itinerary report and
    view), or orderOf (the Resources screen's old order). It has to be passed in rather than
    looked up later, because a booking's own copy of a resource has no place on it, so the place
    is read off the order here and stamped onto the row. Without it every row sorts the same and
    the sections come out alphabetically.

    visible, from fromDaysheetOrder, decides which resources are on the sheet at all. Without it
    each resource's own Show in Itinerary switch does, as it always did (the booking calendar).
  */
  buildRows(bookings: any[], resourceOrder: Map<string, number> = null, visible: Map<string, boolean> = null): itineraryrow[] {
    const rows: itineraryrow[] = [];

    const placeOf = (id: string, name: string): number => this.placeIn(resourceOrder, id, name);

    (bookings ?? []).forEach(booking => {
      const itinerary = this.itineraryText(booking, visible);
      const waiver = this.waiverText(booking);

      this.resourcesOn(booking, visible).forEach(res => {
        rows.push({
          resourceId: res.id,
          resourceName: res.name ?? "",
          displayOrder: placeOf(res.id, res.name ?? ""),
          timeslot: res.time,
          slotSort: this.minutesOf(res.time),
          bookingId: booking.bookingId,
          bookingIdHR: booking.bookingIdHR,
          startTime: this.timeOnly(booking.startTime),
          startDateTime: this.dateAndTime(booking.startTime),
          name: booking.name ?? "",
          email: booking.email ?? "",
          phone: booking.phone ?? "",
          packageName: booking.packageName ?? "",
          packageId: (booking.packageId ?? booking.packageid ?? "").toString(),
          resourceLabel: (res.name ?? "") + " - " + res.time + " - " + res.people,
          endtime: res.endtime,
          itinerary: itinerary,
          guests: Number(booking.guests) || 0,
          people: Number(res.people) || 0,
          admissions: booking.admissions == null
            ? ""
            : (typeof booking.admissions == "string" ? booking.admissions : this.admissionsText(booking.admissions)),
          waiver: waiver,
          extras: this.extrasText(booking.extras),
          outstanding: Number(booking.outstanding) || 0,
          currency: booking.currency ?? "",
          checkedin: booking.checkedin == true,
          slotBookings: 0,
          slotPeople: 0,
          slotCapacity: 0,
          notes: booking.notes ?? "",
          allergy: booking.allergy ?? "",
          publicBooking: booking.publicBooking == true,
          disabled: booking.disabled == true,
          firstOfSlot: false
        });
      });
    });

    return rows;
  }

  //Cuts the rows down to the resources asked for, puts them in reading order and marks where each
  //timeslot sub-heading goes. Returns the rows and what they add up to.
  arrange(allRows: itineraryrow[], chosenResources: any[], resourceOrder: Map<string, number>, search: string = "", capacityOf: Map<string, number> = null): { rows: itineraryrow[], guests: number } {
    //On the id rather than the name, so two resources sharing a name stay apart.
    const wanted = (chosenResources ?? []).map(res => this.idOf(res));
    const term = (search ?? "").trim().toLowerCase();

    const matches = (row: itineraryrow): boolean => {
      if (term == "") {
        return true;
      }

      return [row.name, row.packageName, row.bookingIdHR, row.resourceName, row.email, row.allergy]
        .some(field => (field ?? "").toLowerCase().includes(term));
    };

    //Stamped on at build time, so a row that never found its resource sorts to the end rather
    //than to the front.
    const placeOf = (row: itineraryrow): number => {
      const place = Number(row.displayOrder);

      return isNaN(place) ? Number.MAX_SAFE_INTEGER : place;
    };

    const rows = (allRows ?? [])
      .filter(row => wanted.length == 0 || wanted.includes(row.resourceId))
      .filter(row => matches(row))
      .sort((one, two) => {
        //Sections follow the Resources screen. Anything not on that list - a disabled resource,
        //or one the sheet saw but the list did not - goes after it, by name.
        const byPlace = placeOf(one) - placeOf(two);

        if (byPlace != 0) {
          return byPlace;
        }

        //Two resources with one name are still two sections, so rows of the same id have to
        //stay together.
        const byResource = one.resourceName.localeCompare(two.resourceName)
          || one.resourceId.localeCompare(two.resourceId);

        if (byResource != 0) {
          return byResource;
        }

        if (one.slotSort != two.slotSort) {
          return one.slotSort - two.slotSort;
        }

        return (one.bookingIdHR ?? "").localeCompare(two.bookingIdHR ?? "");
      });

    let previousResource = "";
    let previousSlot = "";

    rows.forEach(row => {
      row.firstOfSlot = row.resourceId != previousResource || row.timeslot != previousSlot;
      row.slotBookings = 0;
      row.slotPeople = 0;
      row.slotCapacity = 0;
      previousResource = row.resourceId;
      previousSlot = row.timeslot;
    });

    //How busy each timeslot is, counted once the rows are in their final order and kept on the
    //row that starts the slot - the one that draws the sub-heading.
    let slotStart: itineraryrow = null;

    rows.forEach(row => {
      if (row.firstOfSlot) {
        slotStart = row;
        slotStart.slotCapacity = this.capacityFor(capacityOf, row) ?? 0;
      }

      slotStart.slotBookings++;
      slotStart.slotPeople += row.people;
    });

    return { rows: rows, guests: rows.reduce((total, row) => total + row.guests, 0) };
  }

  //People covered out of people needing cover. BookingList returns the signed waivers themselves,
  //and one waiver covers the person who signed it plus any children named on it - so a parent
  //signing for two children is three people covered, not one.
  private waiverText(booking: any): string {
    //Waivers do not apply to every package. Where the booking does not take them there is
    //nothing to count, and a hyphen says that rather than 0/2 implying two are outstanding.
    if (booking.haswaiver === false) {
      return "-";
    }

    const waivers = booking.waivers;

    if (!Array.isArray(waivers)) {
      return "";
    }

    const covered = waivers
      .filter((w: any) => w.disabled !== true)
      .reduce((total: number, w: any) => {
        //Someone signing on behalf of others without taking part is not one of the participants.
        const signer = w.isparticipant === false ? 0 : 1;
        const children = Array.isArray(w.children) ? w.children.length : 0;

        return total + signer + children;
      }, 0);

    return covered + "/" + this.participantsOn(booking);
  }

  //Waivers are needed one per participant. Where the booking carries its admissions in full the
  //ones that are not participants are left out; where it only carries a total, that total is used.
  private participantsOn(booking: any): number {
    if (Array.isArray(booking.admissions)) {
      const people = booking.admissions
        .filter((adm: any) => adm.participant == true)
        .reduce((total: number, adm: any) => total + (Number(adm.noofpeople) || 0), 0);

      if (people > 0) {
        return people;
      }
    }

    return Number(booking.guests) || 0;
  }

  //BookingList hands back the resources already opened up, one entry per use. A group that
  //slipped through unopened has no name of its own, so only those are descended into - a named
  //resource that happens to carry children is left as itself.
  private flattenResources(list: any[]): any[] {
    const found: any[] = [];

    (list ?? []).forEach(res => {
      const unnamedGroup = (res.name ?? "").toString().trim() == ""
        && Array.isArray(res.resources) && res.resources.length > 0;

      if (unnamedGroup) {
        found.push(...this.flattenResources(res.resources));
      }
      else {
        found.push(res);
      }
    });

    return found;
  }

  //How many people a resource is booked for. Its own admissions where it has them, the booking's
  //guests otherwise.
  private peopleOn(res: any, booking: any): number {
    if (Array.isArray(res.admissions) && res.admissions.length > 0) {
      const people = res.admissions.reduce((total: number, adm: any) => total + (Number(adm.noofpeople) || 0), 0);

      if (people > 0) {
        return people;
      }
    }

    return Number(booking.guests) || 0;
  }

  //Every use of a resource on this booking, in the order it runs. A booking that goes play,
  //break, play gets three entries, the two plays being the same resource at different times.
  private resourcesOn(booking: any, visible: Map<string, boolean> = null): any[] {
    if (Array.isArray(booking.resources) && booking.resources.length > 0) {
      const opened = this.flattenResources(booking.resources)
        //With the Daysheet order in hand it decides (see fromDaysheetOrder); without it, the
        //resource's own Show in Itinerary switch, as it always did.
        .filter(res => visible != null
          ? this.shownBy(visible, this.idOf(res), this.resourceName(res.name))
          : res.showinitinerary !== false);

      //Every resource it uses is switched off, so the booking is simply not on this sheet - it is
      //not the same as the API sending none, which the placeholder below is for.
      return opened
        .map(res => ({
          id: this.idOf(res),
          name: this.resourceName(res.name),
          time: this.timeOnly(res.startTime),
          endtime: this.timeOnly(res.endTime),
          people: this.peopleOn(res, booking)
        }))
        .sort((one, two) => this.minutesOf(one.time) - this.minutesOf(two.time));
    }

    //returnresources should mean this never happens. Saying so on the row is better than
    //quietly falling back to the resource summary, which is not to be trusted.
    return [{
      id: "",
      name: "(No resources returned)",
      time: this.timeOnly(booking.startTime),
      endtime: "",
      people: Number(booking.guests) || 0
    }];
  }

  //Something is always shown, so a resource that came back without a name still gets a section
  //rather than an unlabelled one.
  private resourceName(value: any): string {
    const name = (value ?? "").toString().trim();

    return name == "" ? "(No resource named)" : name;
  }

  //The itinerary as one block of text, the way the customer's sheet prints it.
  private itineraryText(booking: any, visible: Map<string, boolean> = null): string {
    const lines: string[] = [];

    if (booking.arrival != null) {
      lines.push("Arrival - " + this.timeOnly(booking.arrival) + " - " + (Number(booking.guests) || 0) + " people");
    }

    this.resourcesOn(booking, visible).forEach(res => {
      lines.push(res.name + " - " + res.time + " - " + res.people + " people");
    });

    if (booking.bookingend != null) {
      lines.push("End - " + this.timeOnly(booking.bookingend));
    }

    return lines.join("\n");
  }

  //As the Booking List report prints them: the name and how many of each.
  private extrasText(extras: any[]): string {
    if (!Array.isArray(extras)) {
      return "";
    }

    return extras
      .filter(extra => (Number(extra.qty) || 0) > 0)
      .map(extra => extra.name + " (" + extra.qty + ")")
      .join(", ");
  }

  //Resource id to how many places it holds, for the timeslot sub-headings.
  capacityOf(resources: any[]): Map<string, number> {
    const capacity = new Map<string, number>();

    (resources ?? []).forEach(res => {
      const places = Number(res.concurrentUses) || 0;
      const id = this.idOf(res);

      if (id != "") {
        capacity.set(id, places);
      }

      //By name too, for the same reason the order map is - see orderOf.
      const name = this.nameKey(res?.name);

      if (name != "" && !capacity.has(name)) {
        capacity.set(name, places);
      }
    });

    return capacity;
  }

  //A row's resource capacity from capacityOf, by id then name. Null when the resource is not
  //in it. Capacities are looked up rather than stamped on every row: they are only wanted on
  //the rows that draw a heading.
  capacityFor(capacity: Map<string, number>, row: itineraryrow): number {
    if (capacity == null || row == null) { return null; }

    return capacity.get(row.resourceId) ?? capacity.get(this.nameKey(row.resourceName)) ?? null;
  }

  private admissionsText(admissions: any[]): string {
    return admissions
      .filter(adm => (Number(adm.noofpeople) || 0) > 0)
      .map(adm => adm.noofpeople + " x " + adm.name)
      .join(", ");
  }

  //Times are held as the time written down with a Z on the end, so they are read back off the
  //string rather than through a Date, which would shift them by the browser's offset.
  private timeOnly(value: any): string {
    if (value == null) {
      return "";
    }

    const text = String(value);

    return text.length >= 16 ? text.substring(11, 16) : text;
  }

  private dateAndTime(value: any): string {
    if (value == null) {
      return "";
    }

    const text = String(value);

    if (text.length < 16) {
      return text;
    }

    return text.substring(8, 10) + "/" + text.substring(5, 7) + "/" + text.substring(0, 4) + " " + text.substring(11, 16);
  }

  private minutesOf(time: string): number {
    if (time == null || time.length < 5) {
      return 0;
    }

    return (Number(time.substring(0, 2)) * 60) + Number(time.substring(3, 5));
  }
}
